import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X } from "@digital-twins/design-system/icons";
import { cn } from "@/lib/utils";
import { SocialBrandMark } from "../SocialBrandMark";
import { SOCIAL_PANEL_BOTTOM_PADDING, SocialTopicPanelHeader } from "../SocialTopicPanelHeader";
import { SocialTopicSlotContext } from "../SocialTopicView";
import { useTopicHighlight } from "../SocialTopicHighlightContext";
import { formatSocialCount } from "../socialFormat";
import { useSocialUniverseCounts } from "../useSocialUniverse";
import { SOCIAL_BRAND_FALLBACK_COLOR, socialBrandColors } from "./socialChartColors";
import { shareOfVoice } from "./socialTopicMetrics";

interface SocialShareOfVoiceDumbbellProps {
  /** Reads the brand counts and their `branded` totals; a row click sets the brand filter. */
  context: SocialTopicSlotContext;
}

const TICKS = [25, 50, 75];

/** The axis labels over the plot in the dialog: both ends, and the middle tick from `sm` up. */
const AXIS_LABELS = [0, 50, 100];

/** Where a row sits: the side panel card, or the dialog with its own % columns. */
type DumbbellLayout = "card" | "dialog";

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/**
 * Share of voice per brand: a filled dot for the current measure and a hollow
 * one for the other, over the branded posts. Each brand is its own share (a
 * post can name two brands), so the rows are not parts of one whole. The brand
 * filter is left out of its own counts, so the other brands stay on screen.
 */
export function SocialShareOfVoiceDumbbell(props: SocialShareOfVoiceDumbbellProps) {
  const { context } = props;
  const { universe, topicFilters, refreshKey, brandNames, brandLogoPaths } = context;

  const { t, i18n } = useTranslation("app");
  const [isExpanded, setIsExpanded] = useState(false);
  const { active, setActive } = useTopicHighlight();
  const { metric, brandId: selectedBrandId } = topicFilters.filters;
  const hasBrands = universe.brands.length > 0;
  const filters = useMemo(() => topicFilters.facetFilters("brand"), [topicFilters]);
  const { counts } = useSocialUniverseCounts(
    hasBrands ? universe.token : null,
    { groupBy: "brand", filters },
    refreshKey,
  );

  const labelOf = (brandId: string) => brandNames.get(brandId) ?? brandId;
  const voice = useMemo(
    () =>
      counts
        ? shareOfVoice({
            cells: counts.cells,
            branded: counts.branded,
            metric,
            labelOf: (brandId) => brandNames.get(brandId) ?? brandId,
          })
        : null,
    [counts, metric, brandNames],
  );
  const brandColors = useMemo(() => socialBrandColors(universe.brands), [universe.brands]);
  const colorOf = (brandId: string) => brandColors.get(brandId) ?? SOCIAL_BRAND_FALLBACK_COLOR;

  if (!hasBrands) {
    return null;
  }

  const notAvailable = t("socialListening.topicPage.stats.notAvailable");
  const other: typeof metric = metric === "reach" ? "posts" : "reach";
  const percent = (share: number | null) =>
    share == null ? notAvailable : t("socialListening.topicKpis.percent", { value: Math.round(share) });
  const wholePercent = (share: number | null) =>
    share == null ? notAvailable : String(Math.round(share));
  const isEmpty = voice != null && voice.brandedPosts <= 0;

  /** The mark of a measure, drawn as on the plot: filled for the current one, hollow for the other. Neutral: it keys the shape, not a brand. */
  function renderKey(isFilled: boolean) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-block size-2.5 shrink-0 rounded-full",
          isFilled ? "bg-foreground" : "border-2 border-foreground bg-background",
        )}
      />
    );
  }

  function renderLegend() {
    return (
      <div className="flex items-center gap-2.5" data-testid="social-topic-sov-legend">
        <span className="inline-flex items-center gap-1.5">
          {renderKey(true)}
          {t(`socialListening.topicPage.shareOfVoice.legend.${metric}`)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          {renderKey(false)}
          {t(`socialListening.topicPage.shareOfVoice.legend.${other}`)}
        </span>
      </div>
    );
  }

  function renderRows(layout: DumbbellLayout) {
    if (!voice || isEmpty) {
      return null;
    }
    const isDialog = layout === "dialog";
    return voice.rows.map((row, index) => {
      const brand = labelOf(row.brandId);
      const color = colorOf(row.brandId);
      const isOff = selectedBrandId != null && selectedBrandId !== row.brandId;
      const isDimmed = active?.kind === "brand" && active.brandId !== row.brandId;
      const primary = row.primaryShare == null ? null : clampPercent(row.primaryShare);
      const secondary = row.secondaryShare == null ? null : clampPercent(row.secondaryShare);
      const lo = Math.min(primary ?? secondary ?? 0, secondary ?? primary ?? 0);
      const hi = Math.max(primary ?? secondary ?? 0, secondary ?? primary ?? 0);
      const shares = isDialog ? (
        <>
          <span
            className="text-right text-sm font-semibold tabular-nums text-foreground"
            data-testid={`social-topic-sov-primary-${index}`}
          >
            {percent(row.primaryShare)}
          </span>
          <span
            className="text-right text-sm tabular-nums text-muted-foreground"
            data-testid={`social-topic-sov-secondary-${index}`}
          >
            {percent(row.secondaryShare)}
          </span>
        </>
      ) : (
        <span className="text-right text-sm leading-tight tabular-nums">
          <span
            className="block font-semibold text-foreground"
            data-testid={`social-topic-sov-primary-${index}`}
          >
            {percent(row.primaryShare)}
          </span>
          <span className="block text-xs text-muted-foreground">
            {percent(row.secondaryShare)}
          </span>
        </span>
      );
      return (
        <li key={row.brandId} className={isDialog ? "col-span-4 grid grid-cols-subgrid" : undefined}>
          <button
            type="button"
            data-testid={`social-topic-sov-brand-${index}`}
            aria-pressed={selectedBrandId === row.brandId ? "true" : "false"}
            aria-label={t("socialListening.topicPage.shareOfVoice.rowAria", {
              brand,
              reach: wholePercent(row.reach.share),
              posts: wholePercent(row.posts.share),
            })}
            onClick={() =>
              topicFilters.setFilter(
                "brandId",
                selectedBrandId === row.brandId ? null : row.brandId,
              )
            }
            onMouseEnter={() => setActive({ kind: "brand", brandId: row.brandId })}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive({ kind: "brand", brandId: row.brandId })}
            onBlur={() => setActive(null)}
            className={cn(
              "w-full items-center rounded-md text-left transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              isDialog
                ? "col-span-4 grid grid-cols-subgrid py-2"
                : "grid grid-cols-[6.75rem_minmax(0,1fr)_3rem] gap-2.5 py-0.5",
              (isOff || isDimmed) && "opacity-35",
            )}
          >
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <SocialBrandMark
                logoPath={brandLogoPaths?.get(row.brandId)}
                swatch={null}
                size="row"
                testId={`social-topic-sov-brand-mark-${index}`}
              />
              <span className="truncate text-sm font-medium text-foreground">{brand}</span>
            </span>
            <span className="relative h-[18px]" aria-hidden="true">
              <span className="absolute inset-x-0 top-1/2 h-px bg-card-border" />
              {TICKS.map((tick) => (
                <span
                  key={tick}
                  className="absolute top-[5px] h-2 w-px bg-card-border"
                  style={{ left: `${tick}%` }}
                />
              ))}
              {primary != null || secondary != null ? (
                <span
                  data-testid={`social-topic-sov-connector-${index}`}
                  className="absolute top-1/2 h-0.5 -translate-y-1/2 opacity-35 transition-[left,width] duration-500 ease-out motion-reduce:transition-none"
                  style={{ left: `${lo}%`, width: `${hi - lo}%`, background: color }}
                />
              ) : null}
              {secondary != null ? (
                <span
                  data-testid={`social-topic-sov-hollow-${index}`}
                  className="absolute top-1/2 size-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-background transition-[left] duration-500 ease-out motion-reduce:transition-none"
                  style={{ left: `${secondary}%`, borderColor: color }}
                />
              ) : null}
              {primary != null ? (
                <span
                  data-testid={`social-topic-sov-filled-${index}`}
                  className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0_0_0_2px_hsl(var(--background))] transition-[left] duration-500 ease-out motion-reduce:transition-none"
                  style={{ left: `${primary}%`, background: color }}
                />
              ) : null}
            </span>
            {shares}
          </button>
        </li>
      );
    });
  }

  const footer =
    voice && !isEmpty
      ? t("socialListening.topicPage.shareOfVoice.footer", {
          count: voice.brandedPosts,
          views: formatSocialCount(voice.brandedViews, i18n.language),
        })
      : null;

  function renderList() {
    const rows = renderRows("card");
    return rows ? <ul className="flex flex-col gap-3 px-5 pt-3">{rows}</ul> : null;
  }

  /**
   * The dialog's chart: one grid whose rows share its columns (subgrid), so the
   * column heads (the legend) sit right over the plot and over each % column.
   */
  function renderDialogChart() {
    const rows = renderRows("dialog");
    if (!rows) {
      return null;
    }
    return (
      <div className="grid grid-cols-[minmax(0,5rem)_minmax(0,1fr)_auto_auto] gap-x-2.5 px-4 sm:grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)_minmax(3.5rem,auto)_minmax(3.5rem,auto)] sm:gap-x-4 sm:px-6">
        <div
          className="sticky top-0 z-10 col-span-4 grid grid-cols-subgrid items-center bg-background pb-2 pt-4 text-xs font-medium text-muted-foreground"
          data-testid="social-topic-sov-dialog-columns"
        >
          <span aria-hidden="true" />
          <span className="relative h-4 tabular-nums" aria-hidden="true">
            {AXIS_LABELS.map((tick) => (
              <span
                key={tick}
                className={cn(
                  "absolute top-0 leading-4",
                  tick === 0 && "left-0",
                  tick === 100 && "right-0",
                  tick !== 0 && tick !== 100 && "hidden -translate-x-1/2 sm:inline",
                )}
                style={tick !== 0 && tick !== 100 ? { left: `${tick}%` } : undefined}
              >
                {t("socialListening.topicKpis.percent", { value: tick })}
              </span>
            ))}
          </span>
          {([metric, other] as const).map((measure) => (
            <span
              key={measure}
              className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap"
              data-testid={`social-topic-sov-column-${measure}`}
            >
              {renderKey(measure === metric)}
              {t(`socialListening.topicPage.shareOfVoice.legend.${measure}`)}
            </span>
          ))}
        </div>
        <ul className="col-span-4 grid grid-cols-subgrid pb-3">{rows}</ul>
      </div>
    );
  }

  return (
    <section
      aria-label={t("socialListening.topicPage.shareOfVoice.title")}
      className={cn("flex h-full min-h-0 flex-col", SOCIAL_PANEL_BOTTOM_PADDING)}
      data-testid="social-topic-share-of-voice"
    >
      <SocialTopicPanelHeader
        title={t("socialListening.topicPage.shareOfVoice.title")}
        trailing={renderLegend()}
      />
      <div className="min-h-0 flex-1 overflow-hidden">
        {renderList()}
        {isEmpty ? (
          <p className="px-5 pt-3 text-sm text-muted-foreground" data-testid="social-topic-sov-empty">
            {t("socialListening.topicPage.shareOfVoice.empty")}
          </p>
        ) : null}
      </div>
      {footer ? (
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-card-border px-4 pt-2">
          <p className="min-w-0 truncate text-xs tabular-nums text-muted-foreground" data-testid="social-topic-sov-footer">
            {footer}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 px-2 text-xs"
            data-testid="social-topic-sov-expand"
            aria-expanded={isExpanded}
            aria-haspopup="dialog"
            aria-label={t("socialListening.topicPage.shareOfVoice.expandAria")}
            onClick={() => setIsExpanded(true)}
          >
            {t("socialListening.topicPage.shareOfVoice.expand")}
          </Button>
        </div>
      ) : null}
      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent
          hideClose
          aria-describedby={undefined}
          className="flex max-h-[min(40rem,calc(100dvh-2rem))] w-[calc(100vw-2rem)] max-w-xl flex-col gap-0 overflow-hidden p-0 sm:p-0"
          data-testid="social-topic-sov-dialog"
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-card-border py-3 pl-4 pr-3 sm:pl-6 sm:pr-4">
            <DialogTitle className="text-lg leading-tight">
              <span className="font-title">{t("socialListening.topicPage.shareOfVoice.title")}</span>
            </DialogTitle>
            <DialogClose
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t("close", { ns: "common" })}
              data-testid="social-topic-sov-dialog-close"
            >
              <X className="size-4" aria-hidden="true" />
            </DialogClose>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="social-topic-sov-dialog-list">
            {isExpanded ? renderDialogChart() : null}
          </div>
          {footer ? (
            <p
              className="shrink-0 border-t border-card-border px-4 py-3 text-xs tabular-nums text-muted-foreground sm:px-6"
              data-testid="social-topic-sov-dialog-footer"
            >
              {footer}
            </p>
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
